import { Injectable } from '@angular/core';
import { Router, UrlTree } from '@angular/router';
import { Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import {
  FeatureFlagService,
  FeatureFlags,
} from '../services/feature-flag.service';

/**
 * Keeps `/use-mcp` reachable only where the connector is deployed AND the viewer is entitled.
 *
 * Two gates, deliberately:
 *
 * - `environment.mcpEnabled && environment.mcpUrl` - the page's whole job is to hand a customer a
 *   URL they will paste into an AI assistant, so an environment where `apex-mcp` has not been
 *   deployed must not render it at all; a copyable URL that answers nothing is worse than no page.
 * - the `mcp_connector` feature flag - a per-org/per-user gate, so the connector can go out to a
 *   few customers at a time in an environment where it *is* deployed. Off by default, which means
 *   a failed flag read hides the page rather than exposing it.
 *
 * The sidebar and /profile page hide their entry points on the same two conditions; this guard
 * covers direct-link and bookmark cases. Note this hides the page, it does not restrict the
 * connector: apex-mcp is reachable by anyone who knows the URL and holds valid Apex credentials,
 * and enforces its own OAuth, MFA and subscription checks.
 */
@Injectable({
  providedIn: 'root',
})
export class McpEnabledGuard {
  constructor(
    private readonly router: Router,
    private readonly featureFlags: FeatureFlagService
  ) {}

  canActivate(): Observable<boolean | UrlTree> {
    const dashboard = this.router.createUrlTree(['/dashboard']);
    if (!environment.mcpEnabled || !environment.mcpUrl) return of(dashboard);

    return this.featureFlags.isEnabled(FeatureFlags.MCP_CONNECTOR).pipe(
      map((enabled) => (enabled ? true : dashboard)),
      catchError(() => of(dashboard))
    );
  }
}
